import { z } from 'zod';

/**
 * Metric Hub — activity log entry.
 *
 * Each row captures one mutation on a metric / draft / comment so the
 * dashboard can render a "recent activity" feed and so the Activity tab
 * on a metric detail page can scroll through who-did-what. Lives in a
 * top-level `metrics_activity_log` collection (separate from Data Hub's
 * resource activity log).
 *
 * Indexes used (declared in `firestore.indexes.json`):
 *   - `(resourceType, resourceId, timestamp DESC)` — Activity tab per metric.
 *   - `(resourceType, timestamp DESC)`            — dashboard activity feed.
 */

const isoDateString = z.string().datetime();

export const metricActivityActionSchema = z.enum([
  'metric.create',
  'metric.update',
  'metric.deprecate',
  'metric.restore',
  'metric.lineage.update',
  /** Legacy name — nothing writes it; kept so old rows keep parsing. */
  'metric.health.set',
  /** The owner set or cleared their health advisory (`PATCH …/health`). */
  'metric.health',
  /** The owner acknowledged / withdrew an "expected" on a data warning. */
  'metric.health.ack',
  'metric.health.unack',
  'glossary.create',
  'glossary.update',
  'glossary.delete',
  'glossary.suggest',
  'glossary.accept',
  'glossary.dismiss',
  'draft.create',
  'draft.update',
  'draft.submit',
  'draft.withdraw',
  'draft.approve',
  'draft.request_changes',
  'draft.reject',
  'draft.mark_duplicate',
  'comment.create',
  'comment.resolve',
  'comment.reaction',
  'subscription.set',
  'subscription.clear',
  'reviewer.promote',
  'reviewer.demote',
]);
export type MetricActivityAction = z.infer<typeof metricActivityActionSchema>;

export const metricActivityResourceTypeSchema = z.enum(['metric', 'draft', 'comment', 'user']);
export type MetricActivityResourceType = z.infer<typeof metricActivityResourceTypeSchema>;

export const metricActivitySchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorEmail: z.string().email(),
  actorName: z.string().nullable().default(null),
  actorArea: z.string().nullable().default(null),
  action: metricActivityActionSchema,
  resourceType: metricActivityResourceTypeSchema,
  resourceId: z.string(),
  resourceTitle: z.string().nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({}),
  timestamp: isoDateString,
});
export type MetricActivity = z.infer<typeof metricActivitySchema>;
